import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type MouseEvent,
  type PointerEvent,
} from 'react';

import { nextTapIndex, planTap, prefersReducedMotion, type TapMove } from './faceTap';

/**
 * The avatar: two choices by the person, one reading by the app.
 *
 * | channel      | says               | who decides                    |
 * | ------------ | ------------------ | ------------------------------ |
 * | smile        | 30-day attendance  | the app, from the journal      |
 * | eyes         | which person       | the singer                     |
 * | colour       | which person       | the singer                     |
 *
 * **One shape for everybody since 2026-09-04**, and it is the circle. The
 * outline was the voice part from 2026-08-21 - a circle for the sopranos, a
 * rounded square for the altos, a hexagon for the tenors, a cup for the basses
 * - so that a regent could read the balance of the parts off a wall of faces.
 * The choir's own screen says the same thing better in words: the list is four
 * sections under a heading like «СОПРАНО · 4», and a heading counts where a
 * silhouette had to be learnt first. The tenor's clip-path went with the four,
 * which is why a face can be masked with a border-radius again.
 *
 * There was a fourth channel until 2026-08-24: the weight of the line meant
 * years in the choir, and a filled figure has no line to say it with.
 *
 * **The smile is drawn wherever the face is**, lists included, and that is the
 * owner's decision of 2026-09-03. It was taken out of them earlier the same
 * afternoon, on an argument two outside readings had arrived at separately -
 * a token saying «ходит плохо», repeated down forty rows in front of the whole
 * choir, is a verdict posted on a wall, and this app does not scold; it is in
 * `docs/design-schemes/`. The owner reads the same face the other way round:
 * it is what makes coming worth something, and one that says nothing to forty
 * people motivates nobody. He is the one who watches his choir read it.
 *
 * `rate` stayed optional from that day, because the screen somebody in a pause
 * is left with has no figure to hand it and should not invent one.
 *
 * Only the eyes and the colour are stored, so an avatar cannot lie about its
 * owner: the smile is recomputed on every render and a closed rehearsal moves
 * it the same day. Two of the eight eyes and two of the eight colours are not
 * offered until a streak of rehearsals opens them - see `LADDER` below - and
 * that gate is arithmetic over `best_streak` rather than a stored flag, for the
 * same reason.
 *
 * **A face answers a finger**, since 2026-09-04: one's own preens and somebody
 * else's recoils. The reaction is the component's own business rather than
 * every caller's, which is why the run of taps is state in here and not a prop
 * - a list of forty rows must not hold forty counters for a flourish. What it
 * costs the caller is one boolean, `mine`. See `faceTap.ts` for the arithmetic.
 *
 * Nothing is loaded: there is no file, no upload and no CDN behind any of this.
 */

/** The eyes and the colour, 1-based as they are in the API. */
export const EYES_COUNT = 8;
export const COLOR_COUNT = 8;

/**
 * The sizes the app draws an avatar at, in px, and the arithmetic below reads
 * them: the belt and the face are both shares of the box, so the number cannot
 * live in the stylesheet the way `.avatar-ring`'s used to. This
 * is the one place in the app where a size is a constant in TypeScript rather
 * than a rule in `index.css`, and DESIGN.md records why.
 */
export const AVATAR_ROW = 38;
/** One person's own card: between a roster row and the profile's own face. */
export const AVATAR_CARD = 60;
/** The screen somebody in a pause is left with, where the face is all there is. */
export const AVATAR_LOCKED = 72;
export const AVATAR_LARGE = 64;
/** The arrivals feed is denser than a roster, and nobody in it has a status. */
export const AVATAR_FEED = 26;

/** The glyph inside a picker cell, which is eyes and nothing else. */
export const EYES_GLYPH = 34;

/** What is true of the person right now, drawn as the colour of the belt. */
type AvatarStatus = 'plain' | 'going' | 'not_going';

/**
 * Five steps of smile, and the fifth is sadness below 40%.
 *
 * It reverses «No sad face exists» of 2026-08-21, on the owner's reading of
 * 2026-09-04: a face that says nothing motivates nobody, and the step the
 * previous revision was afraid of is the one that makes the other four mean
 * something. The fear is answered by the floor rather than by the absence of
 * the step - `null` is not sadness, and neither is a pause.
 */
export type Smile = 'wide' | 'smile' | 'half' | 'flat' | 'sad';

/**
 * **The one place on the client where a threshold of the smile is written.**
 *
 * Highest first, each boundary being the lowest share that still draws its
 * step; below the last of them is `sad`, which has no boundary of its own
 * because there is nothing under it.
 *
 * It is a table and not four comparisons because three other places on this
 * client need the same numbers - the sentence «до улыбки ещё 8%», the ticks on
 * the track on «Я», and the step's own name - and each of them had written them
 * out again. Four copies of a rule is four chances for the app to draw one
 * thing and say another about it.
 *
 * There is a fifth copy, and it is on the server: `STEPS` in
 * `app/domain/avatar.py`, which the bot needs because a message saying the face
 * has stopped smiling has to know what a smile is. That one cannot be deleted
 * and this one cannot be either - a face must not wait for a request to know
 * its own mouth - so the two are pinned to each other by
 * `backend/tests/test_smile_thresholds.py`, which reads both files and fails
 * when they disagree. DESIGN.md records why that is the shape of the answer.
 */
export const SMILE_STEPS: readonly (readonly [number, Smile])[] = [
  [92, 'wide'],
  [75, 'smile'],
  [60, 'half'],
  [40, 'flat'],
];

export function smileOf(rate: number | null): Smile {
  // Null is not zero: a window with no rehearsals in it says nothing about the
  // person, so it takes the flat mouth rather than the sad one. «Nothing to
  // say» and «said little» share a shape; «came to two in five» does not.
  if (rate === null) return 'flat';
  const pct = rate * 100;
  return SMILE_STEPS.find(([boundary]) => pct >= boundary)?.[1] ?? 'sad';
}

/** All the glyphs are drawn in this box, whatever the avatar's size is. */
const GRID = 24;

/**
 * Eight pairs of eyes, and this is half of what one person picks about their
 * own face. Everything here is stroked except the dots, which carry
 * `glyph-fill` and are painted instead.
 *
 * The last two are not offered until a streak opens them - see `LADDER`. They
 * are drawn here all the same: a locked cell shows the glyph it would give,
 * because a reward nobody can see is not one.
 */
const EYES: JSX.Element[][] = [
  // 1 · dots
  [
    <circle key="l" cx={8.6} cy={10} r={1.5} className="glyph-fill" />,
    <circle key="r" cx={15.4} cy={10} r={1.5} className="glyph-fill" />,
  ],
  // 2 · lines
  [<path key="l" d="M6.8 10 H10.4" />, <path key="r" d="M13.6 10 H17.2" />],
  // 3 · circles
  [<circle key="l" cx={8.6} cy={10} r={2.2} />, <circle key="r" cx={15.4} cy={10} r={2.2} />],
  // 4 · a squint
  [
    <path key="l" d="M6.6 10.8 Q8.6 8.9 10.6 10.8" />,
    <path key="r" d="M13.4 10.8 Q15.4 8.9 17.4 10.8" />,
  ],
  // 5 · brows over dots
  [
    <circle key="l" cx={8.6} cy={10.4} r={1.4} className="glyph-fill" />,
    <circle key="r" cx={15.4} cy={10.4} r={1.4} className="glyph-fill" />,
    <path key="bl" d="M6.6 6.6 L10.6 7.6" />,
    <path key="br" d="M17.4 6.6 L13.4 7.6" />,
  ],
  // 6 · strokes
  [<path key="l" d="M8.6 8.4 V11.4" />, <path key="r" d="M15.4 8.4 V11.4" />],
  // 7 · «подмигивание», the first rung's
  [
    <path key="l" d="M6.6 9.6 Q8.6 11.6 10.6 9.6" />,
    <circle key="r" cx={15.4} cy={10} r={1.5} className="glyph-fill" />,
  ],
  // 8 · «взгляд», the third rung's: the circles of 3 with a pupil in each
  [
    <circle key="l" cx={8.6} cy={10} r={2.2} />,
    <circle key="r" cx={15.4} cy={10} r={2.2} />,
    <circle key="lp" cx={8.6} cy={10} r={0.9} className="glyph-fill" />,
    <circle key="rp" cx={15.4} cy={10} r={0.9} className="glyph-fill" />,
  ],
];

/**
 * Five steps of smile and one startle, and every one of them is `M + Q + Q`.
 *
 * A path flows into another path segment for segment: one `Q` against two, or
 * an `H` against a curve, has nothing to interpolate and the browser switches
 * instead of moving. The five steps were `M + Q` and `flat` was `M + H`, which
 * is why the mouth snapped rather than flowed - on the tap, and on «Профиль»
 * where a transition had been declared since the day the stage was drawn and
 * had never once had two comparable paths to work between.
 *
 * Each curve is the one that was always drawn, cut in half at its own middle:
 * the shape is identical to the pixel and only the writing changed.
 */
const MOUTHS: Record<Smile | 'gasp', string> = {
  wide: 'M7.4 14.4 Q9.7 17 12 17 Q14.3 17 16.6 14.4',
  smile: 'M8.6 15 Q10.3 16.45 12 16.45 Q13.7 16.45 15.4 15',
  half: 'M9 16.4 Q10.8 17 12.4 16.75 Q14 16.5 15.4 15.4',
  flat: 'M9.2 16.2 Q11 16.2 12 16.2 Q13 16.2 14.8 16.2',
  sad: 'M8.6 17.8 Q10.3 16.2 12 16.2 Q13.7 16.2 15.4 17.8',
  // Nobody's attendance draws this one: a startle, held for the length of a
  // recoil and then given back.
  gasp: 'M10.6 15.6 Q12 13.9 13.4 15.6 Q12 17.8 10.6 15.6',
};

/**
 * What the face does with its own face while it answers, over the mouth
 * attendance gave it.
 *
 * The owner's second look, 2026-09-04: only the circle moved, and a face that
 * is startled ought to look startled. It lasts exactly as long as the movement
 * and then hands the mouth back - a tap still changes nothing, it only answers.
 *
 * A paused face is not in it: it has no mouth to borrow over, and the only run
 * it plays is `wilt`.
 */
const TAP_EXPRESSION: Record<TapMove, { mouth: Smile | 'gasp'; eyes: number }> = {
  preen: { mouth: 'wide', eyes: 0.92 }, // pleased: the mouth widens, the eyes narrow
  recoil: { mouth: 'gasp', eyes: 1.22 }, // startled: a small round mouth, wide eyes
  burst: { mouth: 'wide', eyes: 1.14 },
  wilt: { mouth: 'sad', eyes: 0.9 },
};

/**
 * The share of the box the face takes.
 *
 * Below 40px it takes more of it, .74 against .66: at a roster's size the large
 * ratio leaves the eyes too small to tell one pair from another. The widest
 * smile then adds .04 at either size - the best faces are a touch larger, which
 * is the one place in the system where a reading changes a measurement.
 */
function faceRatio(size: number, smile: Smile): number {
  return (size < 40 ? 0.74 : 0.66) + (smile === 'wide' ? 0.04 : 0);
}

/**
 * One weight for the features at every size, and it says nothing - it is the
 * middle of the three weights that used to mean years in the choir, and the
 * one the picker's cells were already drawn at.
 */
const GLYPH_WEIGHT = 1.9;

/** The eyes alone, which is what the picker's cells show. */
export function EyesGlyph({ eyes, size }: { eyes: number; size: number }): JSX.Element {
  return (
    <svg
      className="glyph"
      width={size}
      height={size}
      viewBox={`0 0 ${GRID} ${GRID}`}
      strokeWidth={GLYPH_WEIGHT}
      aria-hidden="true"
    >
      {EYES[eyes - 1] ?? EYES[0]}
    </svg>
  );
}

/**
 * One run of movement on a face, played once and then over.
 *
 * The four events of the motion table plus the two the list's scheduler uses.
 * A face plays one at a time: a second one arriving replaces the first, which
 * the caller does by giving the box a new `key` rather than by any state here.
 */
export type FaceMove = 'squash' | 'hop' | 'hop-soft' | 'nod' | 'blink-once';

/** `blink-once` is a gesture of the eyes; everything else moves the whole box. */
function movesTheEyes(move: FaceMove): boolean {
  return move === 'blink-once';
}

export default function Avatar({
  eyes,
  color,
  rate,
  size,
  status = 'plain',
  away = false,
  celebrating = false,
  idle = true,
  phase,
  move = null,
  mine = false,
  streak = 0,
  tappable = true,
}: {
  /** 1-based, as in the API. */
  eyes: number;
  color: number;
  /**
   * The 30-day share the smile follows.
   *
   * Omitted and null draw the same mouth and mean two different things, «this
   * screen has no figure to give» and «the window holds no rehearsals», and
   * neither of them is a bad mouth.
   */
  rate?: number | null;
  /**
   * The one face in the app that is about the day rather than about the
   * figures: the row of somebody whose birthday it is. It smiles its widest
   * whatever the last month says, and it asks for that by name rather than by
   * passing a rate of 1, which is what it did until 2026-09-03.
   */
  celebrating?: boolean;
  size: number;
  status?: AvatarStatus;
  /**
   * Out of the choir for a while: the colour drains and the face goes, eyes
   * apart. The smile is thirty days of attendance and there are none, so the
   * right answer is not a calm mouth but no mouth — a face that said «спокойно»
   * about somebody who has not been to a rehearsal since September would be the
   * avatar lying about its owner, which the whole channel model refuses.
   *
   * The eyes stay, because they are the one channel that is theirs: a figure
   * with nothing in it is a placeholder, and this is a person.
   */
  away?: boolean;
  /**
   * Whether the face breathes on its own. Two steps do and three do not: the
   * widest smile blinks and the sad one sighs, the middle three stand still.
   * Off where a face is a still picture of somebody — the picker's own preview
   * before a pick, a row nobody is looking at.
   */
  idle?: boolean;
  /**
   * This face's own clock, from `livenessPhase`. Without it every idle face in
   * a list starts its cycle at the same millisecond and forty of them blink
   * together, which is the one thing the owner asked this not to look like.
   */
  phase?: CSSProperties;
  /** A run of movement, played once. See `FaceMove`. */
  move?: FaceMove | null;
  /**
   * Whose face this is, which is the whole of the difference a tap makes: one's
   * own preens and everybody else's recoils. False by default, because most of
   * the faces in the app belong to somebody else.
   */
  mine?: boolean;
  /**
   * The streak behind this face, for the fifth tap in a run: with one it bursts
   * and without one it only sags. Zero where the screen has no figure to give,
   * which is most of them - a row in a roster carries no streak, and the sag is
   * the honest answer there.
   */
  streak?: number;
  /**
   * Whether a tap on this face is the face's own. Off where the face is already
   * a control and the tap belongs to it: the header on «Я» is the button with
   * the pencil in it, and a face that preened instead of opening «Профиль»
   * would be the app taking a tap and giving back a flourish.
   */
  tappable?: boolean;
}): JSX.Element {
  const smile = celebrating ? 'wide' : smileOf(rate ?? null);
  // The belt is the padding: the outer box carries the status colour and the
  // inner one, inset by the padding, carries the person's own. The span between
  // them is the hairline of `--bg` that keeps the two apart - see `.avatar-gap`.
  // It stayed padding when the shapes went, because the hairline is a third
  // ring and a border cannot draw one.
  const padding = Math.max(2, Math.round(size * 0.075));
  const face = Math.round(size * faceRatio(size, smile));

  // The brows belong to one pair of eyes rather than to the face, so they are
  // dropped by key: «без лица» means the mouth and the brows, and the eyes are
  // what is left.
  const features = (EYES[eyes - 1] ?? EYES[0]).filter(
    (glyph) => !away || (glyph.key !== 'bl' && glyph.key !== 'br'),
  );

  /** The tap being answered right now, or null while the face stands still. */
  const [tap, setTap] = useState<{ move: TapMove; amplitude: number; duration: number } | null>(
    null,
  );
  /** The run this face is in: how many taps it holds, and when the last landed. */
  const run = useRef({ n: 0, at: 0 });
  const box = useRef<HTMLSpanElement>(null);
  const ends = useRef(0);

  // The face always returns exactly where it stood, including on the way out:
  // a timer left behind by a row that scrolled off would set state on nothing.
  useEffect(() => () => window.clearTimeout(ends.current), []);

  // A layout effect and not an ordinary one: an effect runs after the paint, so
  // the first frame of the new run was drawn, then killed and started again -
  // which is a jump at the very start of every answer.
  useLayoutEffect(() => {
    if (tap === null) return;
    const node = box.current;
    if (node === null) return;
    // An animation does not restart when the class that runs it is put back on
    // the same element, and two preens in a row are the same class. Everywhere
    // else in the app the answer is a new `key` from the caller; here the run
    // belongs to the face itself, so the restart does too - and a reflow
    // between taking the animation off and giving it back is what it takes.
    //
    // The name alone and not the `animation` shorthand, which would also reach
    // the delay and the period - two properties React is setting on this box
    // from the style above, and reaching past React into what it owns is how a
    // face ends up with a clock nobody can account for.
    node.style.animationName = 'none';
    void node.offsetHeight;
    node.style.animationName = '';
  }, [tap]);

  function answer(event: MouseEvent<HTMLSpanElement>): void {
    // Somebody who asked for less movement gets none, and gets the row they
    // tapped instead: the face falls back to being part of it, which is what it
    // was before any of this. Swallowing a tap and then doing nothing with it
    // would be the one reading that is worse than no reaction.
    if (prefersReducedMotion()) return;
    // The row this face sits in navigates and the face does not. It swallows
    // the tap and plays; the name, the sub-line and the chevron open the person
    // exactly as they did.
    event.stopPropagation();
    const now = Date.now();
    const n = nextTapIndex(run.current.n, run.current.at, now);
    run.current = { n, at: now };
    const planned = planTap({ n, mine, smile, away, streak });
    setTap({ move: planned.move, amplitude: planned.amplitude, duration: planned.duration });
    window.clearTimeout(ends.current);
    ends.current = window.setTimeout(() => setTap(null), planned.duration);
  }

  /**
   * A row that listens for `pointerdown` gets the event before any click, so
   * swallowing the click alone left a swipe or a long press starting under a
   * finger that had asked the face a question.
   */
  function swallow(event: PointerEvent<HTMLSpanElement>): void {
    if (prefersReducedMotion()) return;
    event.stopPropagation();
  }

  const breathes = idle && !away;
  // A tap replaces whatever was playing: the scheduler's gesture is a flourish
  // nobody asked for and the tap is an answer to a finger, so the finger wins.
  const eyesMove = tap === null && move !== null && movesTheEyes(move);
  const boxMove = tap === null && move !== null && !movesTheEyes(move);
  // What the face wears while it answers. A paused face is left out: it has no
  // mouth to wear an expression over, and the only run it plays is the sag.
  const expression = tap === null || away ? null : TAP_EXPRESSION[tap.move];

  return (
    <span
      ref={box}
      className={
        [
          'avatar-figure',
          status === 'plain' ? null : `avatar--${status}`,
          away ? 'avatar--away' : null,
          // The sigh goes while a tap plays. Two runs on one `transform` and the
          // first of them takes the property: a six-second sigh with a negative
          // delay swallowed the answer whole, and 120ms later the tap fell out
          // of the list and the face jerked and stood still. On a sad face that
          // happened every time.
          tap === null && breathes && smile === 'sad' ? 'avatar-figure--sigh' : null,
          tappable ? 'avatar-figure--tappable' : null,
          boxMove ? `avatar-figure--${move}` : null,
          tap === null ? null : `avatar-figure--${tap.move}`,
        ]
          .filter((name) => name !== null)
          .join(' ')
      }
      // The phase belongs to whichever animation is running on this box, and
      // the sigh is the only idle one that is. `--a` is how wide the tap goes:
      // the keyframes are written at 1 and every offset in them multiplies by
      // it, so one property carries the mood and the run of taps together.
      style={{
        width: size,
        height: size,
        padding,
        ...(smile === 'sad' ? phase : undefined),
        ...(tap === null
          ? undefined
          : ({
              '--a': tap.amplitude,
              // The run's own timing, and it has to be inline because the sad
              // face's sigh is: `livenessPhase` puts a six-second period and a
              // negative delay on this very box, inline beats the class, and
              // without these two a recoil would play over six seconds and
              // start half way through itself.
              animationDelay: '0ms',
              animationDuration: `${tap.duration}ms`,
            } as CSSProperties)),
      }}
      // Decorative still, and a tap changes nothing about it: what answers here
      // is a flourish, not a control, and a screen reader offered «аватар,
      // кнопка» would be promised something it cannot do.
      aria-hidden="true"
      onClick={tappable ? answer : undefined}
      // A row that listens for `pointerdown` - a swipe, a long press - would get
      // the event whatever the click handler above does with its own.
      onPointerDown={tappable ? swallow : undefined}
    >
      {/* The rings the burst throws, and only then: they are the one thing in
          this reaction that is not the face itself moving. Two of them a beat
          apart, because one ring reads as a ring and two read as a spreading. */}
      {tap?.move === 'burst' && (
        <>
          <span className="avatar-wave" />
          <span className="avatar-wave avatar-wave--second" />
        </>
      )}
      <span className="avatar-gap">
        <span className="avatar-body" style={{ background: `var(--avatar-${color})` }}>
          <svg
            className="glyph"
            width={face}
            height={face}
            viewBox={`0 0 ${GRID} ${GRID}`}
            strokeWidth={GLYPH_WEIGHT}
          >
            <g
              className={
                [
                  'avatar-eyes',
                  // The blink stands down while a tap plays, for the reason
                  // the sigh does: the eyes are answering, and an idle gesture
                  // over the answer is two clocks on one pair of eyes.
                  tap === null && breathes && smile === 'wide' ? 'avatar-eyes--blink' : null,
                  eyesMove ? `avatar-eyes--${move}` : null,
                ]
                  .filter((name) => name !== null)
                  .join(' ')
              }
              style={{
                ...(smile === 'wide' ? phase : undefined),
                // Written at rest as well as during an answer: a transform that
                // arrives with the expression animates the way out and snaps on
                // the way back, because there is nothing to come back to.
                ...(tappable ? { transform: `scale(${expression?.eyes ?? 1})` } : undefined),
              }}
            >
              {features}
            </g>
            {!away && <path className="avatar-mouth" d={MOUTHS[expression?.mouth ?? smile]} />}
          </svg>
        </span>
      </span>
    </span>
  );
}
